import { useState, useRef, useCallback, useEffect } from "react";
import { fetchDeepgramToken } from "../api/checkin";
import { isIOS } from "../utils/browserDetect";

interface UseDeepgramTranscriptionOptions {
  language: string;
  checkinToken: string;
}

export function useDeepgramTranscription({
  language,
  checkinToken,
}: UseDeepgramTranscriptionOptions) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const baseTranscriptRef = useRef("");
  const finalSegmentsRef = useRef("");
  const transcriptRef = useRef(transcript);
  transcriptRef.current = transcript;

  // Pre-fetched token cache
  const cachedTokenRef = useRef<{ key: string; fetchedAt: number } | null>(null);

  const isSupported = !!checkinToken;

  // Pre-fetch Deepgram token on mount so it's ready when user taps record
  useEffect(() => {
    if (!checkinToken) return;
    fetchDeepgramToken(checkinToken)
      .then((key) => {
        cachedTokenRef.current = { key, fetchedAt: Date.now() };
      })
      .catch(() => {
        // Silent fail — will retry on startListening
      });
  }, [checkinToken]);

  const getDgKey = useCallback(async (): Promise<string> => {
    // Use cached token if less than 4 minutes old (tokens live 5 min)
    const cached = cachedTokenRef.current;
    if (cached && Date.now() - cached.fetchedAt < 4 * 60 * 1000) {
      return cached.key;
    }
    const key = await fetchDeepgramToken(checkinToken);
    cachedTokenRef.current = { key, fetchedAt: Date.now() };
    return key;
  }, [checkinToken]);

  const cleanup = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;

    if (wsRef.current) {
      // Send empty byte to signal finalization
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(new ArrayBuffer(0));
      }
      wsRef.current.close();
      wsRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    setIsConnecting(false);
    setIsListening(false);
    setInterimTranscript("");
  }, []);

  const startListening = useCallback(async () => {
    if (!checkinToken) {
      setError("No check-in token available");
      return;
    }

    setError(null);
    cleanup();
    setIsConnecting(true);

    // Capture existing transcript as base
    baseTranscriptRef.current = transcriptRef.current;
    finalSegmentsRef.current = "";

    try {
      // Get mic access and Deepgram key in parallel
      const [stream, dgKey] = await Promise.all([
        navigator.mediaDevices.getUserMedia({ audio: true }),
        getDgKey().catch(() => null as string | null),
      ]);

      streamRef.current = stream;

      if (!dgKey) {
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        setIsConnecting(false);
        setError(
          language === "fr"
            ? "Service de transcription indisponible. Utilisez la saisie texte."
            : "Transcription service unavailable. Please use text input."
        );
        return;
      }

      // Determine audio format
      // iOS WebKit may claim webm support but produce invalid chunks — force mp4 on iOS
      const onIOS = isIOS();
      let mimeType = "";
      let dgParams = `language=${language === "fr" ? "fr" : "en"}&model=nova-3&punctuate=true&smart_format=true&interim_results=true&endpointing=false`;

      if (!onIOS && MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
        mimeType = "audio/webm;codecs=opus";
        dgParams += "&encoding=opus&sample_rate=48000";
      } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
        mimeType = "audio/mp4";
        // Deepgram auto-detects mp4/aac — don't pass encoding/sample_rate
      } else if (!onIOS && MediaRecorder.isTypeSupported("audio/webm")) {
        mimeType = "audio/webm";
        dgParams += "&encoding=opus&sample_rate=48000";
      }

      // Open WebSocket to Deepgram
      const ws = new WebSocket(
        `wss://api.deepgram.com/v1/listen?${dgParams}`,
        ["token", dgKey]
      );
      wsRef.current = ws;

      ws.onopen = () => {
        // Start recording once WebSocket is ready
        const recorderOptions: MediaRecorderOptions = {};
        if (mimeType) {
          recorderOptions.mimeType = mimeType;
        }

        const recorder = new MediaRecorder(stream, recorderOptions);
        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0 && ws.readyState === WebSocket.OPEN) {
            ws.send(event.data);
          }
        };

        const chunkMs = mimeType === "audio/mp4" ? 500 : 250;
        recorder.start(chunkMs);
        setIsConnecting(false);
        setIsListening(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const alt = data.channel?.alternatives?.[0];
          if (!alt) return;

          const text = alt.transcript || "";
          if (!text) return;

          if (data.is_final) {
            // Accumulate final segments (Deepgram sends per-utterance, not cumulative)
            finalSegmentsRef.current = (finalSegmentsRef.current + " " + text).trim();
            const base = baseTranscriptRef.current;
            setTranscript((base + " " + finalSegmentsRef.current).trim());
            setInterimTranscript("");
          } else {
            setInterimTranscript(text);
          }
        } catch {
          // Ignore non-JSON messages (e.g. metadata)
        }
      };

      ws.onerror = () => {
        setError(
          language === "fr"
            ? "Erreur de connexion au service de transcription."
            : "Transcription service connection error."
        );
        cleanup();
      };

      ws.onclose = () => {
        // Only set not-listening if we haven't already cleaned up
        if (wsRef.current === ws) {
          setIsConnecting(false);
          setIsListening(false);
          setInterimTranscript("");
        }
      };
    } catch (err) {
      const isDenied =
        err instanceof DOMException && err.name === "NotAllowedError";
      setError(
        isDenied
          ? language === "fr"
            ? "Accès au microphone refusé. Veuillez l'autoriser dans les paramètres de votre navigateur."
            : "Microphone access denied. Please allow it in your browser settings."
          : language === "fr"
            ? "Impossible d'accéder au microphone."
            : "Could not access microphone."
      );
      cleanup();
    }
  }, [checkinToken, language, cleanup, getDgKey]);

  const stopListening = useCallback(() => {
    cleanup();
  }, [cleanup]);

  const resetTranscript = useCallback(() => {
    setTranscript("");
    setInterimTranscript("");
    setError(null);
    finalSegmentsRef.current = "";
    baseTranscriptRef.current = "";
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    isSupported,
    isConnecting,
    isListening,
    transcript,
    interimTranscript,
    error,
    startListening,
    stopListening,
    resetTranscript,
    setTranscript,
  };
}
